/**
 * TeamContext.js
 * Global state management for team selection and permissions in Data-Next
 */

import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DataService from "../services/DataService";
import { AuthContext } from "../../AuthProvider";

const TeamContext = createContext();

export function TeamProvider({ children }) {
  const { user } = useContext(AuthContext);
  const [teams, setTeams] = useState([]);
  const [currentTeam, setCurrentTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState({
    canRead: false,
    canWrite: false,
    canManage: false,
    role: "none",
  });

  const dataService = new DataService();

  // Load teams when user changes
  useEffect(() => {
    if (user) {
      loadTeams();
    } else {
      setTeams([]);
      setCurrentTeam(null);
      setLoading(false);
    }
  }, [user]);

  // Update permissions when team changes
  useEffect(() => {
    if (currentTeam && user) {
      updatePermissions();
    } else {
      setPermissions({
        canRead: false,
        canWrite: false,
        canManage: false,
        role: "none",
      });
    }
  }, [currentTeam, user]);

  const loadTeams = async () => {
    try {
      setLoading(true);
      const userTeams = await dataService.getTeams();
      setTeams(userTeams);

      // Auto-select team from localStorage or first team
      const savedTeamId = localStorage.getItem("data_next_current_team");
      if (savedTeamId && userTeams.find((t) => t.id === savedTeamId)) {
        const team = userTeams.find((t) => t.id === savedTeamId);
        setCurrentTeam(team);
      } else if (userTeams.length === 1) {
        setCurrentTeam(userTeams[0]);
        localStorage.setItem("data_next_current_team", userTeams[0].id);
      }
    } catch (error) {
      console.error("Error loading teams:", error);
    } finally {
      setLoading(false);
    }
  };

  const selectTeam = (teamId) => {
    const team = teams.find((t) => t.id === teamId);
    if (team) {
      setCurrentTeam(team);
      localStorage.setItem("data_next_current_team", teamId);
    }
  };

  const updatePermissions = () => {
    if (!currentTeam || !user) {
      setPermissions({
        canRead: false,
        canWrite: false,
        canManage: false,
        role: "none",
      });
      return;
    }

    const isOwner = currentTeam.owners && currentTeam.owners.includes(user.uid);
    const isCreator =
      currentTeam.creators &&
      currentTeam.creators.includes(user.email.toLowerCase());
    const isReader =
      currentTeam.readers &&
      currentTeam.readers.includes(user.email.toLowerCase());

    setPermissions({
      canRead: isOwner || isCreator || isReader,
      canWrite: isOwner || isCreator,
      canManage: isOwner,
      role: isOwner
        ? "owner"
        : isCreator
        ? "creator"
        : isReader
        ? "reader"
        : "none",
    });
  };

  const createTeam = async (name, description = "") => {
    const teamId = await dataService.createTeam(name, description);
    await loadTeams();
    selectTeam(teamId);
    return teamId;
  };

  const refreshTeams = async () => {
    await loadTeams();
  };

  const value = {
    teams,
    currentTeam,
    loading,
    permissions,
    selectTeam,
    createTeam,
    refreshTeams,
  };

  return <TeamContext.Provider value={value}>{children}</TeamContext.Provider>;
}

export function useTeam() {
  const context = useContext(TeamContext);
  if (!context) {
    throw new Error("useTeam must be used within TeamProvider");
  }
  return context;
}

export default TeamContext;
